import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { getVehiclePath } from '../Utils/vehicleUrls';
import Skeleton from './Skeleton';

const money = (value) => Number(value || 0).toLocaleString('en-US', {
  style: 'currency', currency: 'USD', maximumFractionDigits: 0,
});

export function buildBestDealsViewMoreHref(data) {
  const params = new URLSearchParams();
  if (data?.make) params.set('make', data.make);
  if (data?.model) params.set('model', data.model);
  const scope = data?.searchScope || 'nationwide';
  if (data?.originZip) params.set('zip', data.originZip);
  params.set('radius', scope === '250' || scope === '500' ? scope : '9999');
  return `/insights/best-deals?${params.toString()}`;
}

function LoadingCards() {
  return <div className="mt-4 flex flex-nowrap gap-4 overflow-hidden" data-testid="best-deals-skeletons" aria-hidden="true">
    {Array.from({ length: 6 }).map((_, index) => {
      return <article key={index} data-testid="best-deals-skeleton-card" className="w-[85%] shrink-0 overflow-hidden rounded-xl border border-slate-200 bg-slate-50 sm:w-64 lg:w-72">
        <Skeleton className="aspect-[4/3] w-full rounded-none" />
        <div className="space-y-2 p-3">
          <Skeleton className="h-4 w-5/6" /><Skeleton className="h-4 w-2/3" />
          <Skeleton className="h-6 w-1/2" /><Skeleton className="h-4 w-3/5" />
          <Skeleton className="h-8 w-full rounded-lg" /><Skeleton className="h-4 w-4/5" />
          <Skeleton className="h-4 w-1/2" />
        </div>
      </article>;
    })}
  </div>;
}

function estimateComparison(vehicle) {
  const estimate = Number(vehicle?.mestimate);
  const price = Number(vehicle?.price);
  if (!Number.isFinite(estimate) || estimate <= 0 || !Number.isFinite(price)) return 'Meshum Estimate unavailable';
  const difference = estimate - price;
  if (difference > 0) return `${money(difference)} below ${money(estimate)} estimate`;
  if (difference < 0) return `${money(Math.abs(difference))} above ${money(estimate)} estimate`;
  return `At ${money(estimate)} estimate`;
}

function CurrentCarCard({ car }) {
  if (!car) return null;
  return <article data-testid="best-deals-current-car" className="relative w-[85%] shrink-0 snap-start overflow-hidden rounded-xl border-2 border-blue-300 bg-blue-50/60 sm:w-64 lg:w-72">
    <span className="absolute left-2 top-2 z-10 rounded-full bg-white/95 px-2 py-1 text-xs font-semibold text-blue-800 shadow-sm">This car</span>
    <img src={car.imageUrl} alt={`${car.year} ${car.make} ${car.model}`} className="aspect-[4/3] w-full object-cover" />
    <div className="space-y-2 p-3">
      <div className="line-clamp-2 text-sm font-semibold text-slate-950">{[car.year, car.make, car.model, car.trim].filter(Boolean).join(' ')}</div>
      <div className="text-lg font-semibold text-slate-950">{money(car.price)}</div>
      <div className="text-xs text-slate-600">{car.mileage == null ? 'Mileage unavailable' : `${Number(car.mileage).toLocaleString()} miles`}</div>
      <div className={`rounded-lg px-2 py-1.5 text-xs font-semibold ${Number(car.mestimate) > 0 && Number(car.mestimate) > Number(car.price) ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-600'}`}>{estimateComparison(car)}</div>
      <div className="line-clamp-2 text-xs text-slate-500">{[car.dealerName, car.dealerCity, car.dealerState].filter(Boolean).join(' | ')}</div>
    </div>
  </article>;
}

export default function BestDealsNearby({ carId, currentCar = null }) {
  const [data, setData] = useState(null);
  const [state, setState] = useState('loading');
  const [requestCarId, setRequestCarId] = useState(carId);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const trackedCarId = useRef(null);
  const carouselRef = useRef(null);

  useEffect(() => {
    if (!carId) return undefined;
    const controller = new AbortController();
    setData(null);
    setState('loading');
    setRequestCarId(carId);
    fetch(`${process.env.REACT_APP_API_BASE}/cars/${carId}/best-deals-nearby`, { signal: controller.signal })
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.json();
      })
      .then((response) => {
        if (!controller.signal.aborted) { setData(response); setState('success'); }
      })
      .catch((error) => {
        if (error.name !== 'AbortError') { setData(null); setState('error'); }
      });
    return () => controller.abort();
  }, [carId]);

  const results = useMemo(() => Array.isArray(data?.results) ? data.results : [], [data]);
  const comparisonResults = useMemo(
    () => results.filter((deal) => String(deal.id) !== String(currentCar?.id ?? carId)),
    [carId, currentCar?.id, results]
  );
  useEffect(() => {
    if (!carId || requestCarId !== carId || results.length === 0 || trackedCarId.current === carId) return;
    trackedCarId.current = carId;
    window.gtag?.('event', 'best_deals_nearby_view', { source_car_id: Number(carId), result_count: results.length, search_scope: data.searchScope });
  }, [carId, data, requestCarId, results.length]);

  const updateCarouselControls = useCallback(() => {
    const carousel = carouselRef.current;
    if (!carousel) return;
    setCanScrollLeft(carousel.scrollLeft > 2);
    setCanScrollRight(carousel.scrollLeft + carousel.clientWidth < carousel.scrollWidth - 2);
  }, []);

  useEffect(() => {
    if (state !== 'success') return undefined;
    const frame = window.requestAnimationFrame(updateCarouselControls);
    window.addEventListener('resize', updateCarouselControls);
    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener('resize', updateCarouselControls);
    };
  }, [comparisonResults.length, currentCar?.id, state, updateCarouselControls]);

  const scrollCarousel = (direction) => {
    const carousel = carouselRef.current;
    if (!carousel) return;
    const firstCard = carousel.firstElementChild;
    const distance = firstCard?.getBoundingClientRect().width || Math.max(carousel.clientWidth * 0.8, 280);
    carousel.scrollBy({ left: direction * (distance + 16), behavior: 'smooth' });
  };

  const isLoading = state === 'loading' || requestCarId !== carId;
  if (!isLoading && results.length === 0) {
    if (!currentCar || state !== 'success') return null;
    return <section data-testid="best-deals-nearby" className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <h2 className="text-xl font-semibold text-slate-950">Best Deals Nearby</h2>
      <p className="mt-1 text-sm text-slate-600">Use this vehicle as your comparison starting point.</p>
      <div data-testid="best-deals-mobile-carousel" role="region" aria-label="Vehicle deal comparison" tabIndex="0" className="mt-4 flex flex-nowrap snap-x snap-mandatory gap-4 overflow-x-auto overscroll-x-contain pb-2 [scrollbar-width:thin] [-webkit-overflow-scrolling:touch]">
        <CurrentCarCard car={currentCar} />
      </div>
    </section>;
  }
  if (isLoading) return <section data-testid="best-deals-nearby" className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" aria-busy="true">
    <h2 className="text-xl font-semibold text-slate-950">Best Deals Nearby</h2>
    <p className="mt-1 text-sm text-slate-600">Finding similar vehicles that appear underpriced.</p>
    <span className="sr-only" role="status">Loading Best Deals Nearby</span><LoadingCards />
  </section>;

  const isNearby = data.searchScope === '250';
  const heading = isNearby ? 'Best Deals Nearby' : `Best Deals on Similar ${data.make || ''} ${data.model || ''}`.replace(/\s+/g, ' ').trim();
  const supportingText = isNearby ? `Similar ${data.make} ${data.model} vehicles that appear underpriced.` : data.searchScope === '500' ? 'Best deals found within 500 miles.' : 'Best deals found nationwide.';
  const href = buildBestDealsViewMoreHref(data);
  return <section data-testid="best-deals-nearby" className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
    <div className="flex flex-wrap items-end justify-between gap-3"><div><h2 className="text-xl font-semibold text-slate-950">{heading}</h2><p className="mt-1 text-sm text-slate-600">{supportingText}</p></div>
      <Link to={href} className="text-sm font-semibold text-blue-700 hover:text-blue-800" onClick={() => window.gtag?.('event', 'best_deals_nearby_view_all', { source_car_id: Number(carId), search_scope: data.searchScope })}>View More Best Deals &rarr;</Link></div>
    <div className="mt-3 hidden justify-end gap-2 sm:flex" aria-label="Carousel navigation">
      <button type="button" aria-label="Previous vehicles" disabled={!canScrollLeft} onClick={() => scrollCarousel(-1)} className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-slate-300 bg-white text-lg text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40">&larr;</button>
      <button type="button" aria-label="Next vehicles" disabled={!canScrollRight} onClick={() => scrollCarousel(1)} className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-slate-300 bg-white text-lg text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40">&rarr;</button>
    </div>
    <div ref={carouselRef} onScroll={updateCarouselControls} data-testid="best-deals-mobile-carousel" role="region" aria-label="Vehicle deal comparison" tabIndex="0" className="mt-2 flex flex-nowrap snap-x snap-mandatory gap-4 overflow-x-auto overscroll-x-contain scroll-smooth pb-2 [scrollbar-width:thin] [-webkit-overflow-scrolling:touch]">
      <CurrentCarCard car={currentCar} />
      {comparisonResults.map((deal, index) => <Link key={deal.id} to={getVehiclePath(deal)} className="group w-[85%] shrink-0 snap-start overflow-hidden rounded-xl border border-slate-200 bg-slate-50 no-underline transition hover:-translate-y-0.5 hover:shadow-md sm:w-64 lg:w-72" onClick={() => window.gtag?.('event', 'best_deals_nearby_vehicle_click', { source_car_id: Number(carId), target_car_id: deal.id, result_position: index + 1, same_trim: Boolean(deal.sameTrim), search_scope: data.searchScope })}>
      <img src={deal.imageUrl} alt={`${deal.year} ${deal.make} ${deal.model}`} className="aspect-[4/3] w-full object-cover" loading="lazy" />
      <div className="space-y-2 p-3"><div className="line-clamp-2 text-sm font-semibold text-slate-950">{[deal.year, deal.make, deal.model, deal.trim].filter(Boolean).join(' ')}</div><div className="text-lg font-semibold text-slate-950">{money(deal.price)}</div><div className="text-xs text-slate-600">{deal.mileage == null ? 'Mileage unavailable' : `${Number(deal.mileage).toLocaleString()} miles`}</div><div className="rounded-lg bg-emerald-50 px-2 py-1.5 text-xs font-semibold text-emerald-700">{money(deal.savingsAmount)} below {money(deal.mestimate)} estimate</div><div className="line-clamp-2 text-xs text-slate-500">{[deal.dealerName, deal.dealerCity, deal.dealerState].filter(Boolean).join(' | ')}</div></div>
    </Link>)}</div>
  </section>;
}
